import { pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { checkModuleRight, useModuleRights } from '@work4all/data';

import { FullUser } from '@work4all/models/lib/Classes/FullUser.entity';
import { InputFullUserRelation } from '@work4all/models/lib/Classes/InputFullUserRelation.entity';
import { UserAssignedLicense } from '@work4all/models/lib/Classes/UserAssignedLicense.entity';
import { UserSyncOptions } from '@work4all/models/lib/Classes/UserSyncOptions.entity';
import { ModuleAccessRightType } from '@work4all/models/lib/Enums/ModuleAccessRightType.enum';
import { UserClass } from '@work4all/models/lib/Enums/UserClass.enum';

import { ITabProps, Tabs } from '../../../../navigation/tabs';
import { usePreviewInputsContext } from '../../components/preview-input/hooks';

import { UserExchangeField } from './UserExchangeField';
import { UserLicenseField, UserLicenseUser } from './UserLicenseField';

export const LicenseExchangeTab = () => {
  const [tab, setTab] = useState<'LICENSE' | 'EXCHANGE'>('LICENSE');

  const { entries, isMultiselect, onEdit } = usePreviewInputsContext();
  const update = useLicenseUpdate();

  const { t } = useTranslation();

  const { rights } = useModuleRights();
  const canAccessExchangeConnector4 = checkModuleRight(
    rights,
    ModuleAccessRightType.FEATURE_EXCHANGE_CONNECTOR_4
  );

  const tabs: ITabProps[] = [
    { label: t('USER.LICENSE'), value: 'LICENSE' },
    ...(canAccessExchangeConnector4
      ? [{ label: t('USER.EXCHANGE'), value: 'EXCHANGE' }]
      : []),
  ];

  const user: FullUser = entries[0];
  if (isMultiselect || user.userKind === UserClass.RESSOURCE) return <></>;
  return (
    <div
      style={{
        padding: '1rem',
      }}
    >
      <Tabs
        value={tab}
        handleChange={(_e, value: 'LICENSE' | 'EXCHANGE') => setTab(value)}
        tabs={tabs}
      />
      <div>
        {tab === 'LICENSE' && (
          <UserLicenseField
            value={{ ...user, isLightUser: calculateLightUser(user) }}
            onChange={update}
            readonly
            onUserChange={(change) => {
              // API require empty reletions object
              onEdit(change, { relations: {} });
            }}
          />
        )}
        {tab === 'EXCHANGE' && (
          <UserExchangeField
            value={user}
            onChange={(opt) => update(opt, 'sync')}
            onUserChange={(change) => {
              // API require empty reletions object
              onEdit(change, { relations: {} });
            }}
          />
        )}
      </div>
    </div>
  );
};

const useLicenseUpdate = () => {
  const { onEdit } = usePreviewInputsContext();

  return (
    input: UserAssignedLicense | UserSyncOptions,
    type: 'lic' | 'sync' = 'lic'
  ) => {
    const relations: InputFullUserRelation = {};
    if (type === 'lic')
      relations.setUserAssignedLicense = [pick(input, ['license', 'assigned'])];
    if (type === 'sync')
      relations.syncOptions = {
        addModify: [input],
      };

    onEdit({}, { relations });
  };
};

export const calculateLightUser = (user: UserLicenseUser) => {
  return (
    user.userKind === UserClass.MITARBEITER &&
    user.assignedLicenses.some(
      (license) => license.assigned && license.license.includes('LIGHT')
    )
  );
};
